import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/SignIn.css'; // we'll rename the CSS file too
import API_BASE_URL from '../config';
import { setAuth } from '../auth';
import useSafeBack from '../hooks/useSafeBack';

function SignIn() {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const safeBack = useSafeBack('/');

  // Get the role passed from the role selection page (default to 'technician')
  const { role } = location.state || { role: 'technician' };

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setApiError('');

    // Determine which endpoint to call based on role
    const endpoint = role === 'technician' ? '/technician/login' : '/hirer/login';

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // FastAPI uses { detail: "..." } and validation errors under { detail: [...] }
        const detail = data?.detail;
        if (typeof detail === 'string' && detail.trim()) setApiError(detail);
        else setApiError('Login failed');
        setLoading(false);
        return;
      }

      const token = data.token || 'logged_in';

      if (role === 'technician') {
        setAuth('technician', token, {
          role: 'technician',
          id: data.id,
          email: formData.email,
          full_name: data.full_name,
          status: data.status,
          tech_type: data.tech_type,
        });
        if (data.status === 'approved') {
          navigate('/technician/dashboard');
        } else if (data.status === 'denied') {
          navigate('/technician/denied');
        } else {
          navigate('/technician/pending');
        }
      } else {
        setAuth('hirer', token, {
          role: 'hirer',
          id: data.id,
          email: formData.email,
          full_name: data.full_name || '',
        });
        navigate('/hirer/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setApiError('Network error – please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <button className="back-button" onClick={safeBack}>← Back</button>
      <h2>Sign In {role === 'technician' ? '(Technician)' : '(Hirer)'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <div className="error">{errors.email}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <div className="password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {errors.password && <div className="error">{errors.password}</div>}
        </div>
        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      {apiError && <div className="error api-error">{apiError}</div>}
      <p className="register-prompt">
        Don't have an account?{' '}
        <span
          className="link"
          onClick={() => navigate(`/${role}/register`)}
        >
          Register here
        </span>
      </p>
    </div>
  );
}

export default SignIn;